<template>
    <div class="jobFilter">
        <div class="jobFilter__container p-lg-4 p-2 mb-lg-5 mb-4">
            <div class="jobFilter__tags">
                <div
                    class="tag resetTags"
                    :class="[{'active' : !filterDepartments.length}]"
                >
                    <input
                        @change.prevent="filterDepartments = []"
                        type="checkbox"
                        id="resetTags"
                        name="resetTags"
                        value=""
                        hidden
                    >
                    <label for="resetTags">{{translationStrings.jobFilterResetTags}}</label>
                </div>
                <div
                    class="tag"
                    v-for="tag in jobDepartments"
                    :key="tag"
                    :class="[{'active' : filterDepartments.includes(tag)}]"
                >
                    <input v-model="filterDepartments" type="checkbox" :id="tag" :name="tag" :value="tag" hidden>
                    <label :for="tag">{{ tag }} <div class="plus"></div></label>
                </div>
            </div>

            <div class="jobFilter__search">
                <input :placeholder="translationStrings.jobFilterSearch" type="text" v-model="searchPhrase" />
                <i class="icon-MagnifyingGlass"></i>
            </div>
        </div>

        <div class="JobList">
            <div class="JobList__group mb-4" v-for="(group, key) in jobGroups" :key="key">
                <span class="label lg mb-0">{{ key }}</span>

                <a class="job" :href="job.permalink" v-for="job in group" :key="job.id">
                    <div class="JobList__container p-lg-4 p-2">
                        <div class="JobList__container__content">
                            <span class="label xs mb-1">{{ job.jobDepartment }}</span>
                            <span class="h4 mb-1">{{ job.jobTitle }}</span>
                            <div class="job__meta mt-2">
                                <span v-if="job.jobLocation" class="location">{{ job.jobLocation }}</span>
                                <span v-if="job.jobSchedule" class="schedule">{{ job.jobSchedule }}</span>
                            </div>
                        </div>
                        <i class="icon-CaretRight"></i>
                    </div>
                </a>

            </div>
        </div>
    </div>
</template>

<script>
import {
    computed,
    ref
} from 'vue/dist/vue.esm-bundler';
import { groupBy } from '../modules/utilities';

export default {
    name: 'JobFilter',
    props: {
        jobs: {
            type: String,
            default: ''
        }
    },
    setup(props) {
        const searchPhrase = ref('');
        const filterDepartments = ref([]);
        const jobDepartments = ref([]);
        const { translationStrings } = wpUrls;

        const filteredJobs = computed(() => {
            let jobs = [];
            if (props.jobs !== '') {
                jobs = JSON.parse(props.jobs);

                if (filterDepartments.value.length) {
                    jobs = jobs.filter((job) => filterDepartments.value.includes(job.jobDepartment));
                }
                if (searchPhrase.value !== '' && searchPhrase.value.length >= 3) {
                    // eslint-disable-next-line max-len
                    jobs = jobs.filter((job) => job.post_title.toLowerCase().includes(searchPhrase.value.toLowerCase()));
                }
            }
            return jobs;
        });

        const jobGroups = computed(() => {
            if (filteredJobs.value.length) {
                return groupBy(filteredJobs.value, 'jobDepartment');
            }
            return {};
        });

        if (Object.keys(jobGroups).length) {
            jobDepartments.value = Object.keys(jobGroups.value);
        }

        return {
            jobGroups,
            jobDepartments,
            filterDepartments,
            searchPhrase,
            translationStrings,
        };
    }
};
</script>

<style lang="scss">
    @import '../../../scss/hm/hmVars';

    .jobFilter__container {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        flex-direction: column;
        gap: 32px;
        position: relative;
        z-index: 1;
        background: $white;
        border: 1px solid $black8;
        box-shadow: 0px 0px 64px $black8;
        border-radius: 16px;
        margin-top: -96px;

        .jobFilter__tags {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: flex-end;
            gap: 8px;
            flex: 1 1 50%;

            .tag {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;
                gap: 12px;
                font-weight: 700;
                font-size: .875rem;
                line-height: 40px;
                height: 40px;
                color: $accent;
                background: $white;
                border: 2px solid $accent16;
                border-radius: 120px;
                transition: all .3s ease;

                label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 0 8px 0 20px !important;
                    margin: 0 !important;
                    cursor: pointer;
                }

                .plus {
                    position: relative;
                    width: 24px;
                    height: 24px;
                    background: $accent8;
                    border-radius: 48px;
                    transition: all .3s ease;

                    &::after, &::before {
                        content: '';
                        position: absolute;
                        left: 50%;
                        top: 50%;
                        width: 8px;
                        height: 1px;
                        border-radius: 1px;
                        background-color: $accent;
                        transform: translate(-50%, -50%);
                        transition: all .3s ease;
                    }

                    &::after {
                        transform: translate(-50%, -50%) rotate(90deg);
                    }
                }

                &:hover {
                    background: $accent16;
                }

                &.active {
                    color: $white;
                    background: $accent;
                    border: 2px solid $accent;

                    .plus {
                        background: $white8;

                        &::before, &::after {
                            background-color: $white;
                        }

                        &::after {
                            transform: translate(-50%, -50%) rotate(135deg);
                        }

                        &::before {
                            transform: translate(-50%, -50%) rotate(45deg);
                        }
                    }
                }

                &.resetTags {
                    label {
                        padding: 0 20px !important;
                    }
                }
            }
        }

        .jobFilter__search {
            position: relative;
            width: fit-content;

            i {
                position: absolute;
                top: 23px;
                right: 24px;
                color: $accent;
            }
        }

        @media (min-width: 1200px) {
            flex-direction: row;
            align-items: center;
            border-radius: 32px;
        }
    }

    .JobList {
        .JobList__group {
            display: flex;
            flex-direction: column;
            gap: 32px;

            & > a {
                margin-top: 0;
                margin-bottom: 0;
            }
        }

        .JobList__container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: $white;
            border: 1px solid $black8;
            box-shadow: 0 0 64px $black8;
            border: 2px solid transparent;
            border-radius: 16px;
            transition: all .3s ease;

            i {
                font-size: 1.2rem;
            }

            @media (min-width: 1200px) {
                border-radius: 32px;

                i {
                    font-size: 1.8rem;
                    transition: transform .3s ease;
                }

                &:hover {
                    transition-duration: 0s;
                    border-color: $accent;
                    box-shadow: 0 0 0 8px $accent16, 0 0 32px $accent8, 0 0 64px $accent8;

                    i {
                        transform: translate(3px, 0);
                    }
                }
            }
        }
    }
</style>
